import React, { useState, forwardRef, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Divider,
  Collapse,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';

import LoadingDots from '../LoadingDots/LoadingDots';
import RollingNumber from '../Loaders/RollingNumber';
import SkeletonLoaderWrapper from '../Loaders/SkeletonLoaderWrapper';

const InfoIcon = 'https://assets.askhippo.co.za/Icons/common/info.svg';

const GAP_COVER_TOOLTIP =
  'Gap cover helps pay the shortfall between what your medical aid covers and the actual cost of in-hospital treatment.';

const GapInfoTooltip = () => (
  <Tooltip
    title={<span>{GAP_COVER_TOOLTIP}</span>}
    placement="right"
    arrow
    componentsProps={{
      tooltip: {
        sx: {
          fontFamily: 'Inter, sans-serif',
          fontWeight: 400,
          fontSize: '0.875rem',
          lineHeight: '1.25rem',
          letterSpacing: '-0.002em',
          borderRadius: '12px',
          border: '2px solid #E3E800',
          backgroundColor: '#FFFFFF',
          p: '16px',
          boxShadow: '0px 3px 8px #162F3B1F, 0px 1px 2px #A2ACB133',
          maxWidth: '297px',
          color: '#000',
          '& .MuiTooltip-arrow': { color: '#E3E800' },
        },
      },
      arrow: { sx: { color: '#E3E800' } },
    }}
  >
    <img
      src={InfoIcon}
      alt="Info"
      style={{ width: 16, height: 16, cursor: 'pointer' }}
    />
  </Tooltip>
);

const renderValueWithBoldCurrency = (text) => {
  if (!text) return null;
  const str = String(text);
  // Match SA Rand currency patterns like "R210 580" or "R22,000"
  const match = str.match(/(R\s?\d[\d\s,.\d]*)/i);
  if (match) {
    const amount = match[1];
    const parts = str.split(amount);
    return (
      <>
        {parts[0]}
        <Box component="span" sx={{ fontWeight: 700 }}>
          {amount}
        </Box>
        {parts[1]}
      </>
    );
  }
  return str;
};

const HDivider = (props) => (
  <Divider
    sx={{
      borderColor: '#E0E0E0',
      mx: '16px',
    }}
    {...props}
  />
);

const GridVDivider = ({ sx = {} }) => (
  <Box
    sx={{
      width: '1px',
      backgroundColor: '#E0E0E0',
      alignSelf: 'stretch',
      minHeight: '1px',
      ...sx,
    }}
  />
);

// --- helpers for tabbed section ---
function getValueByPath(obj, path) {
  if (!obj || !path) return undefined;
  return path
    .split('.')
    .reduce(
      (acc, part) => (acc && acc[part] != null ? acc[part] : undefined),
      obj
    );
}
function ensureRandPrefix(val) {
  if (val == null) return '';
  const s = String(val).trim();
  if (!s) return '';
  const numStr = s.replace(/^R?\s*/i, '').replace(/[\s,]/g, '');
  const n = Number(numStr);
  if (!isNaN(n) && numStr !== '') return `R ${n.toLocaleString('en-US')}`;
  return s.replace(/^R?\s*/i, 'R ');
}

function calcAnnualFromMonthlyInt(val) {
  const monthly = Number(val) || 0;
  const annual = monthly * 12;
  return ensureRandPrefix(String(annual));
}

const ProviderTileComponent_MedicalAid = forwardRef(
  (
    {
      data,
      initialLoadDone,
      onSelectQuote,
      bestDealContentReady = true,
      wasLoadingOnMount = useRef(!bestDealContentReady),
    },
    ref
  ) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const isBestDealLoading = data.bestDeal && !bestDealContentReady;
    const hasGap = (data.premium?.monthlypremium_gap ?? 0) > 0;
    const [tab, setTab] = useState(0);
    const [showChronicList, setShowChronicList] = useState(false);

    const handleSelectQuote = (evt) => {
      onSelectQuote(evt, { action: 'select-quote', data });
    };

    // ---- Tabs config (as requested) ----
    const tabs = [
      {
        title: 'Hospital Cover',
        rows: [
          { label: 'Hospital Limit', key: 'hospitalcover.limit.description' },
          { label: 'Hospital Choice', key: 'hospitalcover.choice.description' },
          {
            label: 'Specialist Bills',
            key: 'hospitalcover.rate.moreinfo',
          },
          { label: 'Co-Payments', key: 'hospitalcover.choice.moreinfo' },
          {
            label: 'Hospital Exclusions',
            key: 'hospitalcover.exclusions.description',
          },
        ],
        afterRows: null,
      },
      {
        title: 'Day-to-Day Cover',
        rows: [
          {
            label: 'Day to day Provider',
            key: 'daytodaycover.provider.description',
            belowValueKey: 'daytodaycover.provider.moreinfo',
          },
          { label: 'Day to day benefit', key: 'daytodaycover.moreinfo' },
          {
            label: 'Annual day to day',
            key: 'premium.monthlypremium_daytoday',
            transform: (v) => calcAnnualFromMonthlyInt(v),
            disableCurrencyBold: true,
          },
          {
            label: 'Summary',
            key: 'daytodaycover.generalinfo.summary.text',
          },
        ],
        afterRows: null,
      },
      {
        title: 'Other Benefits',
        rows: [
          {
            label: 'Chronic medicine provider',
            key: 'chroniccover.providertype.description',
            belowValueKey: 'chroniccover.dsp.moreinfo',
          },
          {
            label: 'Chronic conditions',
            key: 'chroniccover.description',
            renderBelow: () => {
              const list = getValueByPath(data, 'chroniccover.conditions');
              const hasList = Array.isArray(list) && list.length > 0;
              return (
                <Box sx={{ mt: '6px' }}>
                  {hasList && (
                    <>
                      <Button
                        variant="text"
                        onClick={() => setShowChronicList((s) => !s)}
                        sx={{
                          p: 0,
                          minWidth: 0,
                          textDecoration: 'underline',
                          textTransform: 'none',
                        }}
                      >
                        {showChronicList ? 'Hide List' : 'Show Me'}
                      </Button>
                      <Collapse in={showChronicList} unmountOnExit>
                        <Box
                          component="ul"
                          sx={{ pl: '18px', mt: '6px', mb: 0 }}
                        >
                          {list.map((it, idx) => (
                            <li key={idx}>
                              <Typography
                                sx={{
                                  fontFamily: 'Inter',
                                  fontWeight: 400,
                                  fontSize: '0.875rem',
                                  lineHeight: '1.275rem',
                                  color: '#162F3B',
                                }}
                              >
                                {String(it)}
                              </Typography>
                            </li>
                          ))}
                        </Box>
                      </Collapse>
                    </>
                  )}
                </Box>
              );
            },
          },
          {
            label: 'Preventative care cover',
            key: 'additionalbenefits.preventativecare.level',
          },
          {
            label: 'Summary',
            key: 'additionalbenefits.preventativecare.description',
            disableCurrencyBold: true,
          },
        ],
        afterRows: null,
      },
      {
        title: 'Cost Breakdown',
        rows: [
          {
            label: 'Risk',
            key: 'premium.monthlypremium_risk',
            transform: (v) => ensureRandPrefix(v),
            boldKey: false,
            disableCurrencyBold: true,
          },
          {
            label: 'Day to day',
            key: 'premium.monthlypremium_daytoday',
            transform: (v) => ensureRandPrefix(v),
            boldKey: false,
            disableCurrencyBold: true,
          },
          {
            label: 'Total Monthly Premium',
            key: 'premium.monthlypremium_total',
            transform: (v) => ensureRandPrefix(v),
            boldKey: true,
            boldValue: true,
          },
        ],
        afterRows: null,
      },
    ];

    // row renderer used by tab content
    const renderRow = ({ label, value, options = {} }) => {
      const { belowValue, isBlockHeader, boldKey, boldValue, customBelowNode } =
        options;
      return (
        <Box
          key={label}
          sx={{
            backgroundColor: '#F6F6F6',
            border: '1px solid #E1E1E1',
            borderRadius: '12px',
            width: '100%',
            maxWidth: '869px',
            minHeight: isBlockHeader ? 'auto' : '52px',
            display: 'grid',
            gridTemplateColumns: '140px 1fr',
            alignItems: isBlockHeader ? 'start' : 'center',
            columnGap: '32px',
            px: '24px',
            py: '16px',
            boxSizing: 'border-box',
          }}
        >
          {/* Header column */}
          <Typography
            sx={{
              fontFamily: 'Inter',
              fontWeight: boldKey ? 700 : 600,
              fontSize: '0.875rem',
              lineHeight: '1.275rem',
              color: '#162F3B',
            }}
          >
            {label}
          </Typography>

          {/* Value column */}
          <Box>
            {isBlockHeader ? (
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontWeight: 400,
                  fontSize: '0.875rem',
                  lineHeight: '1.275rem',
                  color: '#162F3B',
                  textAlign: 'left',
                }}
              >
                {options.disableCurrencyBold
                  ? String(value ?? '')
                  : renderValueWithBoldCurrency(value)}
              </Typography>
            ) : (
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontWeight: boldValue ? 700 : 400,
                  fontSize: '0.875rem',
                  lineHeight: '1.275rem',
                  color: '#162F3B',
                  textAlign: 'left',
                }}
              >
                {options.disableCurrencyBold
                  ? String(value ?? '')
                  : renderValueWithBoldCurrency(value)}
              </Typography>
            )}
            {belowValue && (
              <Typography
                sx={{
                  mt: '6px',
                  fontFamily: 'Inter',
                  fontWeight: 400,
                  fontSize: '0.8125rem',
                  lineHeight: '1.2rem',
                  color: '#425869',
                }}
              >
                {String(belowValue)}
              </Typography>
            )}
            {customBelowNode}
          </Box>
        </Box>
      );
    };

    // build active tab rows
    const active = tabs[tab];
    const rowsToRender = active.rows
      .map((r) => {
        const raw = getValueByPath(data, r.key);
        if (raw == null || String(raw).trim() === '') return null;

        const transformed =
          typeof r.transform === 'function' ? r.transform(raw) : raw;
        const options = {
          belowValue: r.belowValueKey
            ? getValueByPath(data, r.belowValueKey)
            : undefined,
          isBlockHeader: !!r.isBlockHeader,
          boldKey: !!r.boldKey,
          boldValue: !!r.boldValue,
          disableCurrencyBold: !!r.disableCurrencyBold,
          customBelowNode:
            typeof r.renderBelow === 'function' ? r.renderBelow() : undefined,
        };

        return { label: r.label, value: transformed, options };
      })
      .filter(Boolean);

    return (
      <Box
        ref={ref}
        data-testid="provider-tile"
        sx={{
          width: '909px',
          minHeight: data.bestDeal && initialLoadDone ? '236px' : '0px',
          borderRadius: '22px',
          backgroundColor: '#FFFFFF',
          boxShadow: '0px 5px 15px 0px #00000026',
          border:
            data.bestDeal && initialLoadDone ? '2px solid #2DC8CB' : 'none',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* ===== 1) Best Deal banner (own group) ===== */}
        {data.bestDeal && initialLoadDone && (
          <Box
            sx={{
              backgroundColor: '#2DC8CB',
              borderTopLeftRadius: '18px',
              borderTopRightRadius: '18px',
              px: '18px',
              py: '12px',
              textAlign: 'center',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontWeight: 700,
                fontSize: '1.250rem',
                lineHeight: '120%',
                letterSpacing: '-0.03em',
                color: '#FFFFFF',
              }}
            >
              {bestDealContentReady ? (
                'Your Best Deal!'
              ) : (
                <>
                  Calculating Your Best Deal <LoadingDots variant="buy-now" />
                </>
              )}
            </Typography>
          </Box>
        )}

        {/* ===== 2) Main row group: Logo | | Content | | Premium/Actions ===== */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns:
              '16px 210px 16px 1px 17px 277px 15px 1px 16px auto',
            alignItems: 'stretch',
            py: data.bestDeal && initialLoadDone ? '12px' : '16px',
          }}
        >
          {/* left gutter */}
          <Box />

          {/* Logo */}
          <Box
            sx={{
              minHeight: '100px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: '12px',
            }}
          >
            <SkeletonLoaderWrapper loading={isBestDealLoading}>
              <img
                src={data.schemeinfo.logo.normal}
                alt={data.schemename}
                style={{
                  maxWidth: '100%',
                  maxHeight: '76px',
                  objectFit: 'contain',
                }}
              />
            </SkeletonLoaderWrapper>
          </Box>

          {/* gutter before VD #1 */}
          <Box />

          {/* VD #1 at 242px from left */}
          <GridVDivider />

          {/* gutter after VD #1 */}
          <Box />

          {/* Content */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: '6px',
            }}
          >
            <SkeletonLoaderWrapper loading={isBestDealLoading}>
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  lineHeight: '24px',
                  color: '#1E354E',
                }}
              >
                {data.schemename}
              </Typography>
            </SkeletonLoaderWrapper>
            <SkeletonLoaderWrapper loading={isBestDealLoading}>
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontWeight: 400,
                  fontSize: '1rem',
                  lineHeight: '28px',
                  mt: '-6px',
                  color: '#738289',
                }}
              >
                {data.optionname}
              </Typography>
            </SkeletonLoaderWrapper>

            {data.bestDeal && initialLoadDone && (
              <SkeletonLoaderWrapper
                loading={isBestDealLoading}
                skeletonProps={{ width: '90%' }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    rowGap: '0px',
                    columnGap: '12px',
                    mt: '12px',
                    mb: '24px',
                    alignItems: 'center',
                    width: '100%',
                    maxWidth: '360px',
                  }}
                >
                  {[
                    {
                      label: 'Hospital Cover',
                      value: data?.hospitalcover?.limit?.star_rating,
                    },
                    {
                      label: 'Day-to-Day',
                      value: data?.daytodaycover?.star_rating,
                    },
                    {
                      label: 'Chronic Med.',
                      value: data?.chroniccover?.dsp?.star_rating,
                    },
                    {
                      label: 'Doctor Range',
                      value: data?.daytodaycover?.providerchoice?.star_rating,
                    },
                  ].map(({ label, value }) => {
                    const rating = Math.max(
                      0,
                      Math.min(5, parseFloat(value ?? 0))
                    );
                    const railW = 82;
                    const barW = (rating / 5) * railW;
                    return (
                      <React.Fragment key={label}>
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            color: '#738289',
                            fontWeight: 400,
                            letterSpacing: '-0.02em',
                          }}
                        >
                          {label}
                        </Typography>
                        <Box
                          aria-label={`${label} rating ${rating}/5`}
                          role="img"
                          sx={{
                            position: 'relative',
                            width: `${railW}px`,
                            height: '8px',
                          }}
                        >
                          <Box
                            sx={{
                              position: 'absolute',
                              left: 0,
                              right: 0,
                              top: '50%',
                              height: '1px',
                              backgroundColor: '#E3E6E8',
                              opacity: 1,
                              borderRadius: '2px',
                              transform: 'translateY(-50%)',
                            }}
                          />
                          <Box
                            sx={{
                              position: 'absolute',
                              left: 0,
                              top: '50%',
                              width: `${barW}px`,
                              height: '8px',
                              backgroundColor: '#E3E800',
                              opacity: 1,
                              borderRadius: '2px',
                              transform: 'translateY(-50%)',
                            }}
                          />
                        </Box>
                      </React.Fragment>
                    );
                  })}
                </Box>
              </SkeletonLoaderWrapper>
            )}
          </Box>

          {/* gutter before VD #2 */}
          <Box />

          {/* VD #2 at 551.5px from left */}
          <GridVDivider />

          {/* gutter after VD #2 */}
          <Box />

          {/* Premium / actions */}
          <Box
            sx={{
              position: 'relative',
              minHeight: '100px',
              mb: data.bestDeal && initialLoadDone ? '60px' : 0,
              height: '100%',
            }}
          >
            {/* Chips */}
            {Array.isArray(data.RetailChips) && data.RetailChips.length > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  pointerEvents: 'none',
                }}
              >
                {data.RetailChips.map((chip, idx) => (
                  <Box key={idx}>
                    <SkeletonLoaderWrapper loading={isBestDealLoading}>
                      <Box
                        key={idx}
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          color: '#162F3B',
                          backgroundColor: chip.color,
                          letterSpacing: '-0.02em',
                          lineHeight: '1.125rem',
                          borderRadius: '6px',
                          px: '8px',
                          py: '2px',
                          display: 'inline-block',
                        }}
                      >
                        {chip.text}
                      </Box>
                    </SkeletonLoaderWrapper>
                  </Box>
                ))}
              </Box>
            )}

            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '4px',
                  mt: data.bestDeal && initialLoadDone ? '-40px' : '0px',
                }}
              >
                <Typography
                  sx={{
                    mt: '8px',
                    fontFamily: 'Inter',
                    fontWeight: 400,
                    fontSize: '1rem',
                    color: '#A4ACB0',
                  }}
                >
                  From
                </Typography>
                <Box
                  sx={{ display: 'flex', alignItems: 'baseline', mt: '-12px' }}
                >
                  <RollingNumber
                    value={
                      hasGap
                        ? data.premium.monthlypremium_medicalaid
                        : data.premium.monthlypremium_total
                    }
                    prefix={{
                      text: 'R',
                    }}
                    suffix={{
                      text: 'pm',
                      sx: {
                        fontSize: '1.25rem',
                        fontWeight: 700,
                        ml: '2px',
                        color: '#1E354E',
                      },
                    }}
                    digitSx={{
                      fontFamily: 'Inter',
                      fontWeight: 700,
                      fontSize: '3rem',
                      letterSpacing: '-0.02em',
                      color: '#1E354E',
                    }}
                    wrapperSx={{
                      mt: '2px',
                    }}
                    extraLoops={3}
                    playSignal={
                      data.bestDeal && wasLoadingOnMount.current ? 1 : 0
                    }
                    animate={data.bestDeal && wasLoadingOnMount.current}
                  />
                </Box>
                {hasGap && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      mt: '-5px',
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Inter',
                        fontWeight: 400,
                        fontSize: '1rem',
                        lineHeight: '1.5rem',
                        letterSpacing: '-0.002em',
                        color: '#A2ACB1',
                      }}
                    >
                      R{' '}
                      {Number(data.premium.monthlypremium_total).toLocaleString(
                        'en-US'
                      )}{' '}
                      with Gap Cover
                    </Typography>
                    <GapInfoTooltip />
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Fixed-position CTA */}
        {data.bestDeal && initialLoadDone ? (
          <Button
            data-testid="quote-cta-select"
            onClick={handleSelectQuote}
            sx={{
              position: 'absolute',
              right: '16px',
              top: '190px',
              borderRadius: '18px',
              gap: '8px',
              px: '16px',
              py: '8px',
              backgroundColor: '#FF0074',
              border: '1px solid #FF0074',
              boxShadow: '0px 1px 2px 0px #1018280D',
              textTransform: 'none',
              minWidth: 0,
              '&:hover': { backgroundColor: '#e60067' },
              ...(isBestDealLoading && {
                backgroundColor: '#D5D5D5 !important',
                borderColor: '#D5D5D5 !important',
                color: '#7A7A7A !important',
                cursor: 'not-allowed',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: '#D5D5D5 !important',
                },
              }),
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontWeight: 600,
                fontSize: '1rem',
                lineHeight: '24px',
                letterSpacing: '-0.02em',
                color: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              I&apos;m Interested
              <span
                className="material-symbols-rounded"
                style={{ fontSize: '1.5rem', color: '#FFFFFF' }}
              >
                double_arrow
              </span>
            </Typography>
          </Button>
        ) : (
          <Button
            data-testid="quote-cta-select"
            onClick={handleSelectQuote}
            sx={{
              position: 'absolute',
              right: '16px',
              top: '76px',
              backgroundColor: '#FF0074',
              borderRadius: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textTransform: 'none',
              minWidth: 0,
              px: '16px',
              py: '8px',
              boxSizing: 'border-box',
              '&:hover': { backgroundColor: '#e60067' },
            }}
          >
            <span
              className="material-symbols-rounded"
              style={{ fontSize: '1.5rem', color: '#FFFFFF' }}
            >
              double_arrow
            </span>
          </Button>
        )}

        {/* Fixed-position Expand/Collapse */}
        <Button
          onClick={() => setIsExpanded((p) => !p)}
          sx={{
            position: 'absolute',
            top: `${data.bestDeal && initialLoadDone ? 67 : 16}px`,
            right: '16px',
            width: '48px',
            height: '48px',
            p: '8px',
            minWidth: 0,
            backgroundColor: 'transparent',
            boxShadow: 'none',
            '&:hover': { backgroundColor: 'transparent' },
          }}
          aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
        >
          <span
            className="material-symbols-rounded"
            style={{
              fontWeight: 500,
              fontSize: '1.5rem',
              color: '#000',
              transform: isExpanded ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.3s ease',
              display: 'inline-block',
            }}
          >
            keyboard_arrow_down
          </span>
        </Button>

        <Collapse
          in={isExpanded}
          timeout={{ enter: 300, exit: 300 }}
          unmountOnExit
        >
          {/* ===== 3) Divider under main row ===== */}
          <HDivider sx={{ mt: '16px' }} />

          {/* ===== 4) Description group (full width) ===== */}
          <Box
            sx={{
              pl: '32px',
              py: '16px',
              pr: '32px',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: '16px',
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '1rem',
                  mb: 1,
                  color: '#1E354E',
                }}
              >
                Description
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: '32px' }}>
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: '0.875rem',
                    color: '#1E354E',
                    width: '100%',
                    wordWrap: 'break-word',
                    pr: '16px',
                  }}
                >
                  {data.schemedescription}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* ===== 5) Horizontal divider ===== */}
          <HDivider />

          {/* ===== 6) Bottom group: Tabs + rows ===== */}
          <Box sx={{ pl: '32px', py: '16px' }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              sx={{
                mb: '12px',
                '.MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  color: '#1E354E',
                },
              }}
            >
              {tabs.map((t) => (
                <Tab key={t.title} label={t.title} />
              ))}
            </Tabs>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1px 24px',
                alignItems: 'stretch',
                mb: '16px',
              }}
            >
              <Box sx={{ pr: '16px' }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr',
                    rowGap: 1.5,
                  }}
                >
                  {rowsToRender.map((r) => renderRow(r))}
                </Box>

                {/* After-rows paragraph (Preventative care description on Tab 3) */}
                {active.afterRows}
              </Box>

              <Box />
            </Box>
          </Box>

          {/* ===== 7) Footer group: FSP info (optional) ===== */}
          {data.FSPLine && (
            <Box sx={{ px: '16px', py: '12px' }}>
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontWeight: 400,
                  fontSize: '0.875rem',
                  lineHeight: '140%',
                  color: '#B3B3B3',
                  mt: '-4px',
                  mx: '16px',
                  mb: '8px',
                }}
              >
                {data.FSPLine}
              </Typography>
            </Box>
          )}
        </Collapse>
      </Box>
    );
  }
);

ProviderTileComponent_MedicalAid.displayName =
  'ProviderTileComponent_MedicalAid';
export default ProviderTileComponent_MedicalAid;
